import React from "react";
import {
  Text as RNText,
  TextProps as RNTextProps,
  TextStyle,
} from "react-native";
import { cva } from "@louise/utils/cva";
import { twMerge } from "tailwind-merge";

type Variant = "display" | "title" | "body" | "caption";

type SizeByVariant = {
  display: "base" | "lg" | "xl";
  title: "sm" | "base" | "lg";
  body: "sm" | "base" | "md" | "lg";
  caption: "standard" | "caps";
};

type Size<V extends Variant> = SizeByVariant[V];

export type TextProps<V extends Variant = "body"> = Omit<
  RNTextProps,
  "style"
> & {
  variant?: V;
  size?: Size<V>;
  style?: TextStyle | TextStyle[];
  className?: string;
};

const FONT_FAMILY = "Olly";
type AnySize = "base" | "lg" | "xl" | "sm" | "md" | "standard" | "caps";

const textClasses = cva({
  base: [
    "font-sans",
    "font-normal",
    "text-[14px]",
    "text-louise-brand",
    "dark:text-white",
  ],
  variants: {
    variant: {
      display: "",
      title: "",
      body: "",
      caption: "",
    },
    size: {
      base: "",
      sm: "",
      md: "",
      lg: "",
      xl: "",
      standard: "",
      caps: "",
    },
  },
  defaultVariants: {
    variant: "body",
    size: "base",
  },
  compoundVariants: [
    // display (Medium)
    {
      variant: "display",
      size: "base",
      className: "text-2xl leading-9 font-medium",
    },
    {
      variant: "display",
      size: "lg",
      className: "text-3xl leading-10 font-medium",
    },
    {
      variant: "display",
      size: "xl",
      className: "text-4xl leading-tight font-medium",
    },

    // title
    {
      variant: "title",
      size: "sm",
      className: "text-base leading-6 font-medium",
    },
    {
      variant: "title",
      size: "base",
      className: "text-lg leading-6 font-medium",
    },
    {
      variant: "title",
      size: "lg",
      className: "text-xl leading-7 font-semibold",
    },

    // body
    {
      variant: "body",
      size: "sm",
      className: "text-xs leading-5 font-normal",
    },
    {
      variant: "body",
      size: "base",
      className: "text-sm leading-5 font-normal",
    },
    {
      variant: "body",
      size: "md",
      className: "text-sm leading-5 font-medium",
    },
    {
      variant: "body",
      size: "lg",
      className: "text-base leading-6 font-normal",
    },

    // caption
    {
      variant: "caption",
      size: "standard",
      className: "text-xs leading-3 font-normal",
    },
    {
      variant: "caption",
      size: "caps",
      className: "text-xs leading-3 font-normal uppercase",
    },
  ],
});

export function Text<V extends Variant = "body">({
  variant = "body" as V,
  size,
  style,
  className,
  ...rest
}: TextProps<V>) {
  // Provide default size per variant
  const defaultSizeByVariant: Record<Variant, string> = {
    display: "base",
    title: "base",
    body: "base",
    caption: "standard",
  };

  const effectiveSize =
    (size as string) ?? defaultSizeByVariant[variant as Variant];
  const classes = textClasses({
    variant: variant as Variant,
    size: effectiveSize as AnySize,
  });

  return (
    <RNText {...rest} className={twMerge(classes, className)} style={[style]} />
  );
}

export default Text;
